import { Link } from "expo-router";
import {
  BanIcon,
  BoltIcon,
  Building2Icon,
  CheckIcon,
  InboxIcon,
  LogOutIcon,
  MailIcon,
  NotebookTextIcon,
  Users2Icon,
} from "lucide-react-native";
import React, { memo } from "react";
import { Platform } from "react-native";

import RowButton from "@/components/molecules/RowButton";
import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import {
  Popover,
  PopoverBackdrop,
  PopoverBody,
  PopoverContent,
} from "@/components/atoms/Popover";
import { Text } from "@/components/atoms/Text";
import { TeamSelector } from "@/features/team-selection";
import { ifWeb } from "@/utils/platform";

/**
 * Props for HeaderPopoverView component
 */
interface Props {
  isOpen: boolean;
  organizationName?: string;
  onClose: () => void;
  onOpen: () => void;
  onDeactivateMe: () => void;
  onSignOut: () => void;
  onSignOutAllDevices: () => void;
  isAdmin: boolean;
  isSmallScreen: boolean;
  numberOfValidOrganizations: number;
  hasPendingInvitations: boolean;
  isManageMenuOpen: boolean;
  isDark: boolean;
  mode: "light" | "dark" | "system";
  themeIcon: React.ElementType;
  themeLabel: string;
  onThemeToggle: () => void;
  languageLabel: string;
  languageIcon: React.ElementType;
  languageOptions: readonly { code: string; label: string }[];
  languageHandlers: Record<string, () => void>;
  selectedLanguageCode: string | undefined;
  isLanguageMenuOpen: boolean;
  onLanguageMenuToggle: () => void;
  versionString: string;
  onVersionPress: () => void;
  renderPopoverTrigger: (triggerProps: object) => React.JSX.Element;
  renderManageMenuTrigger: () => React.JSX.Element;
  manageItemHandlers: ManageItemHandlers;
}

/**
 * Whether the manage section should be visible
 * @param isAdmin - Whether user is admin
 * @param organizationName - Current organization name
 * @param hasPendingInvitations - Whether user has pending invitations
 * @param numberOfValidOrganizations - Number of valid organizations
 * @returns Whether manage section should render
 */
const shouldShowManageSection = (
  isAdmin: boolean,
  organizationName: string | undefined,
  hasPendingInvitations: boolean,
  numberOfValidOrganizations: number
): boolean =>
  isAdmin ||
  organizationName !== "Default" ||
  hasPendingInvitations ||
  numberOfValidOrganizations > 1;

/**
 * Pre-built press handlers for each manage route, avoiding inline
 * arrow functions that violate react-perf/jsx-no-new-function-as-prop
 */
interface ManageItemHandlers {
  onPlayerRoster: () => void;
  onUsers: () => void;
  onInvitations: () => void;
  onOrganizations: () => void;
  onReceivedInvitations: () => void;
  onNotificationSettings: () => void;
}

/**
 * Render the expanded manage section items
 * Uses pre-built handlers instead of Link navigation to bypass nested
 * Stack navigator interception on web (SE-3726). Link asChild is kept
 * for semantic anchor tags (accessibility, right-click) but onPress
 * overrides the default navigation with History API pushState + popstate.
 * @param props - Subset of Props needed for manage items
 * @param props.isAdmin - Whether user is admin
 * @param props.organizationName - Current organization name
 * @param props.numberOfValidOrganizations - Number of valid organizations
 * @param props.hasPendingInvitations - Whether user has pending invitations
 * @param props.handlers - Pre-built press handlers for each manage route
 * @param props.isDark - Whether dark mode is active
 * @param props.onClose - Closes the popover; passed to TeamSelector so picking or dismissing a team also closes this menu
 * @returns VStack of manage item links
 */
const renderManageItems = (props: {
  isAdmin: boolean;
  organizationName: string | undefined;
  numberOfValidOrganizations: number;
  hasPendingInvitations: boolean;
  handlers: ManageItemHandlers;
  isDark: boolean;
  onClose: () => void;
}): React.JSX.Element => (
  <Stack>
    <TeamSelector onDismiss={props.onClose} />
    {props.isAdmin ? (
      <Link asChild href="/player-roster">
        <RowButton
          topBorderTone="default"
          label="Manage Roster"
          rightIcon={NotebookTextIcon}
          isDark={props.isDark}
          onPress={props.handlers.onPlayerRoster}
        />
      </Link>
    ) : null}
    {props.organizationName !== "Default" ? (
      <Link asChild href="/users">
        <RowButton
          topBorderTone="default"
          label="Manage Users"
          rightIcon={Users2Icon}
          isDark={props.isDark}
          onPress={props.handlers.onUsers}
        />
      </Link>
    ) : null}
    {props.organizationName !== "Default" ? (
      <Link asChild href="/invitations">
        <RowButton
          topBorderTone="default"
          label="Manage Invitations"
          rightIcon={MailIcon}
          isDark={props.isDark}
          onPress={props.handlers.onInvitations}
        />
      </Link>
    ) : null}
    {props.numberOfValidOrganizations > 1 ? (
      <Link asChild href="/organizations">
        <RowButton
          topBorderTone="default"
          label="Switch Orgs"
          rightIcon={Building2Icon}
          isDark={props.isDark}
          onPress={props.handlers.onOrganizations}
        />
      </Link>
    ) : null}
    {props.isAdmin ? (
      <Link asChild href="/organizations">
        <RowButton
          topBorderTone="default"
          label="Manage Orgs"
          rightIcon={Building2Icon}
          isDark={props.isDark}
          onPress={props.handlers.onOrganizations}
        />
      </Link>
    ) : null}
    {props.hasPendingInvitations ? (
      <Link asChild href="/received-invitations">
        <RowButton
          topBorderTone="default"
          label="View Pending Invitations"
          rightIcon={InboxIcon}
          isDark={props.isDark}
          onPress={props.handlers.onReceivedInvitations}
        />
      </Link>
    ) : null}
    <Link asChild href="/notification-settings">
      <RowButton
        label="Notification Setting"
        rightIcon={BoltIcon}
        isDark={props.isDark}
        onPress={props.handlers.onNotificationSettings}
        topBorderTone="default"
      />
    </Link>
  </Stack>
);

/**
 * Pure view component for header popover menu
 * @param root0 - Component props
 * @param root0.isOpen - Whether popover is open
 * @param root0.onClose - Handler for popover close
 * @param root0.onOpen - Handler for popover open
 * @param root0.onDeactivateMe - Handler for account deactivation
 * @param root0.onSignOut - Handler for sign out of the current device only (default)
 * @param root0.onSignOutAllDevices - Handler for sign out of all devices (with confirmation)
 * @param root0.organizationName - Current organization name
 * @param root0.isAdmin - Whether user is admin
 * @param root0.isSmallScreen - Whether device has small screen
 * @param root0.numberOfValidOrganizations - Number of valid organizations
 * @param root0.hasPendingInvitations - Whether user has pending invitations
 * @param root0.isManageMenuOpen - Whether manage section is expanded
 * @param root0.isDark - Whether dark theme is active
 * @param root0.mode - Current theme mode
 * @param root0.themeIcon - Icon component for theme
 * @param root0.themeLabel - Label for theme
 * @param root0.onThemeToggle - Handler for theme toggle
 * @param root0.languageLabel - Label for the app-wide Language row
 * @param root0.languageIcon - Icon component for the Language row
 * @param root0.languageOptions - Available language options
 * @param root0.languageHandlers - Pre-built handlers for each language option
 * @param root0.selectedLanguageCode - Option to mark selected, or undefined if unknown
 * @param root0.isLanguageMenuOpen - Whether the language submenu is expanded
 * @param root0.onLanguageMenuToggle - Handler to toggle the language submenu
 * @param root0.versionString - Formatted version string for display
 * @param root0.onVersionPress - Handler for version press (shows debug info)
 * @param root0.renderPopoverTrigger - Memoized render function for popover trigger button
 * @param root0.renderManageMenuTrigger - Memoized render function for manage menu trigger
 * @param root0.manageItemHandlers - Pre-built press handlers for each manage route, using History API on web to bypass nested Stack interception (SE-3726)
 */
const HeaderPopoverView = ({
  isOpen,
  onClose,
  onOpen,
  onDeactivateMe,
  onSignOut,
  onSignOutAllDevices,
  organizationName,
  isAdmin,
  isSmallScreen: _isSmallScreen,
  numberOfValidOrganizations,
  hasPendingInvitations,
  isManageMenuOpen,
  isDark,
  mode: _mode,
  themeIcon,
  themeLabel,
  onThemeToggle,
  languageLabel,
  languageIcon,
  languageOptions,
  languageHandlers,
  selectedLanguageCode,
  isLanguageMenuOpen,
  onLanguageMenuToggle,
  versionString,
  onVersionPress,
  renderPopoverTrigger,
  renderManageMenuTrigger,
  manageItemHandlers,
}: Props) => (
  <Popover
    isOpen={isOpen}
    onClose={onClose}
    onOpen={onOpen}
    placement="bottom"
    offset={8}
    crossOffset={Platform.OS === "android" ? -88 : 16}
    size="lg"
    useRNModal
    trigger={renderPopoverTrigger}
  >
    <PopoverBackdrop />
    <PopoverContent
      key={`popover-${isDark ? "dark" : "light"}`}
      className={`${ifWeb("min-w-48", "min-w-64")} overflow-hidden rounded-md border-0 p-0 ${isDark ? "bg-surface-action" : "bg-white"}`}
    >
      <PopoverBody className="p-0">
        {/* Header — org name with tinted background for visual hierarchy */}
        <Box className="bg-surface-subtle px-4 pb-3 pt-4">
          <Stack direction="horizontal" className="items-center gap-2">
            <Icon as={Building2Icon} size="sm" className="text-content-muted" />
            <Heading
              variant="title"
              className={`${ifWeb("text-title", "text-title-lg")} text-content-primary`}
            >
              {organizationName}
            </Heading>
          </Stack>
        </Box>

        {/* Thin accent divider separating header from actions */}
        <Box className="h-px bg-outline-default" />

        {/* Menu actions */}
        <Stack className="py-1">
          {/* Manage section — collapsible within the popover to avoid nested overlay z-index issues */}
          {shouldShowManageSection(
            isAdmin,
            organizationName,
            hasPendingInvitations,
            numberOfValidOrganizations
          ) ? (
            <>
              {renderManageMenuTrigger()}
              {isManageMenuOpen
                ? renderManageItems({
                    isAdmin,
                    organizationName,
                    numberOfValidOrganizations,
                    hasPendingInvitations,
                    handlers: manageItemHandlers,
                    isDark,
                    onClose,
                  })
                : null}
            </>
          ) : null}

          {/*
            SE-6509: ungated. This row used to sit behind VOICE_DICTATION; it
            is now the app-wide Language control and a dictation flag cannot
            switch it off. Placement is unchanged - top level, between Manage
            and Theme - which is where the side panel now puts it too, so the
            two entry points match.
          */}
          <RowButton
            label={languageLabel}
            rightIcon={languageIcon}
            isDark={isDark}
            onPress={onLanguageMenuToggle}
            topBorderTone="default"
            testID="settings-language-row"
          />
          {isLanguageMenuOpen ? (
            <Stack>
              {languageOptions.map(option => {
                const isSelected = option.code === selectedLanguageCode;
                return (
                  <RowButton
                    key={option.code}
                    label={option.label}
                    isDark={isDark}
                    onPress={languageHandlers[option.code]}
                    topBorderTone="default"
                    rightIcon={isSelected ? CheckIcon : undefined}
                    accessibilityState={{ selected: isSelected }}
                    testID={`settings-language-option-${option.code}`}
                    indented
                  />
                );
              })}
            </Stack>
          ) : null}

          <RowButton
            label={themeLabel}
            rightIcon={themeIcon}
            isDark={isDark}
            onPress={onThemeToggle}
            topBorderTone="default"
          />

          <RowButton
            label="Deactivate"
            rightIcon={BanIcon}
            isDark={isDark}
            onPress={onDeactivateMe}
            topBorderTone="default"
          />
          <RowButton
            label="Sign Out"
            rightIcon={LogOutIcon}
            isDark={isDark}
            onPress={onSignOut}
            topBorderTone="default"
          />
          <RowButton
            label="Sign Out of All Devices"
            rightIcon={LogOutIcon}
            isDark={isDark}
            onPress={onSignOutAllDevices}
            topBorderTone="default"
            destructive
          />
        </Stack>

        {/* Version footer */}
        <Box className="h-px bg-outline-default" />
        <Pressable onPress={onVersionPress}>
          <Box className="px-4 py-2">
            <Text variant="micro" className="shrink text-content-subtle">
              {versionString}
            </Text>
          </Box>
        </Pressable>
      </PopoverBody>
    </PopoverContent>
  </Popover>
);

HeaderPopoverView.displayName = "HeaderPopoverView";

export default memo(HeaderPopoverView);
